import 'regenerator-runtime/runtime';
import {
  call, put, fork, takeEvery,
} from 'redux-saga/effects';
import { delay } from 'redux-saga';
import { parseError } from 'logic/helpers';
import * as constants from 'constants/redux/conversation';
import * as actionCreators from 'actions/conversation';
import api from '../api';

const mapTags = ({ value, label }) => ({ id: value, tag: label });

function* fetchConversationRequest(req) {
  const maxTryAgain = 3;
  for (let i = 1; i <= maxTryAgain; i++) {
    try {
      const { conversationId } = req.payload;
      const conversation = yield call(api.fetchConversation.bind(null, conversationId));
      yield put(actionCreators.fetchConversationSuccess(conversation));
      return;
    } catch (e) {
      if (i === maxTryAgain || e.status === 401) {
        if (e.status === 401) {
          yield [
            put(actionCreators.fetchConversationFailure({ error: {} })),
          ];
        } else if (e.status === 400) {
          yield put(actionCreators.fetchConversationFailure({
            error: {
              status: e.status,
              message: 'Invalid conversation.',
              canTryAgain: false,
            },
          }));
        } else {
          yield put(actionCreators.fetchConversationFailure({
            error: {
              status: e.status,
              message: 'Could not load conversation.',
              canTryAgain: true,
            },
          }));
        }
        return;
      }
      yield call(delay, 2000);
    }
  }
}

function* postMessageRequest(req) {
  // If post message fails, please retry for 2 more times, before throwing an error
  // https://github.com/redux-saga/redux-saga/issues/349
  const maxTryAgain = 3;
  for (let i = 1; i <= maxTryAgain; i++) {
    try {
      const { conversationId, text } = req.payload;
      const postMsg = yield call(api.postMessage.bind(null, conversationId, text));
      yield [
        put(actionCreators.postMessageSuccess(postMsg)),
        put(actionCreators.fetchConversationRequest({ conversationId: postMsg.conversation_id })),
      ];
      return;
    } catch (e) {
      if (i === maxTryAgain || e.status === 401) {
        if (e.status === 401) {
          yield [
            put(actionCreators.postMessageFailure({ error: {} })),
          ];
        } else {
          yield put(actionCreators.postMessageFailure({
            error: {
              status: e.status,
              message: 'Could not send your message.',
              canTryAgain: true,
            },
          }));
        }
        return;
      }
      yield call(delay, 2000);
    }
  }
}

function* deleteMessageRequest(req) {
  try {
    const { conversationId, messageId } = req.payload;
    yield call(api.deleteMessage.bind(null, conversationId, messageId));

    yield [
      put(actionCreators.deleteMessageSuccess()),
      put(actionCreators.fetchConversationRequest({ conversationId })),
    ];
  } catch (e) {
    yield put(actionCreators.deleteMessageFailure(parseError(e)));
  }
}

function* postMessageSeenRequest(req) {
  try {
    const { conversationId, messageId } = req.payload;
    yield call(api.postMessageSeen.bind(null, conversationId, messageId));
  } catch (e) {
    yield put(actionCreators.deleteMessageFailure(parseError(e)));
  }
}

function* fetchConversationFile(req) {
  try {
    const { conversationId, resourceId } = req.payload;
    const { signedUrl } = yield call(api.fetchConversationFile
      .bind(null, conversationId, resourceId));

    // Download file
    const link = document.createElement('a');

    link.setAttribute('style', 'display: none');
    link.href = signedUrl;
    document.body.appendChild(link);
    link.click();
  } catch (e) {
    yield put(actionCreators.deleteMessageFailure(parseError(e)));
  }
}

function* conversationUploadFilePostInfo(req) {
  try {
    const { conversationId, publicUrl, fileName } = req.payload;
    const postUploadFileInfo = yield call(api.postInfoConversationFileUpload
      .bind(null, conversationId, publicUrl, fileName));
    yield [
      put(actionCreators.conversationUploadFileSuccess(postUploadFileInfo)),
      put(actionCreators.fetchConversationRequest({ conversationId })),
    ];
  } catch (e) {
    if (e.status === 401) {
      yield [
        put(actionCreators.conversationUploadFileFailure({ error: {} })),
      ];
    } else {
      yield put(actionCreators.conversationUploadFileFailure({
        error: {
          status: e.status,
          message: 'Could not upload the file.',
          canTryAgain: true,
        },
      }));
    }
  }
}

function* postConversationTagsRequest(req) {
  try {
    const { id, tags, showArchivedMsg } = req.payload;
    yield call(api.postConversationTags.bind(null, id, tags.map((tag) => tag.value)));

    yield put(actionCreators.postConversationTagsSuccess({
      tags: tags.map(mapTags),
      showArchivedMsg,
    }));
  } catch (e) {
    yield put(actionCreators.postConversationTagsFailure(parseError(e)));
  }
}

export default [
  fork(takeEvery, constants.FETCH_CONVERSATION_REQUEST, fetchConversationRequest),
  fork(takeEvery, constants.DELETE_MESSAGE_REQUEST, deleteMessageRequest),
  fork(takeEvery, constants.POST_MESSAGE_REQUEST, postMessageRequest),
  fork(takeEvery, constants.POST_MESSAGE_SEEN_REQUEST, postMessageSeenRequest),
  fork(takeEvery, constants.FETCH_CONVERSATION_FILE, fetchConversationFile),
  fork(takeEvery, constants.CONVERSATION_UPLOAD_FILE_POST_INFO, conversationUploadFilePostInfo),
  fork(takeEvery, constants.POST_CONVERSATION_TAGS_REQUEST, postConversationTagsRequest),
];
